import { ChartType } from "@components/charts";
import { TChartValueFormatterFn } from "@components/charts/Charts.types";
import {
    DashboardTileType,
    IChartTileInfo,
    IDashboardDefinition,
    IGetTileDataArgs,
    ILinkTileInfo,
    ITableTileData,
    TBasicLinkTileConfig,
    TTileDefinition
} from "@components/dashboard";
import { encodeQueryParam, getDrillDownNavParams, getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { GrowthNegativeIcon, GrowthPositiveIcon, GrowthZeroIcon } from "@components/icon";
import {
    formatDateToDateString,
    getSlidingInterval,
    IDateInterval,
    IDayjsInterval
} from "@components/inputs/date/utils";
import { ifAll, IGetValueArgs, not } from "@components/smart/FieldInfo";
import { IReportRowDef } from "@components/smart/smartTable";
import { IColumn, IRow, TCellValue } from "@components/table";
import { IChartTileData } from "@components/tiles/chartTile/ChartTile";
import { infoTileCurrencyFormatter } from "@components/tiles/infoTile";
import BindingContext, { createPath } from "@odata/BindingContext";
import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    BusinessPartnerEntity,
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    EntitySetName,
    EntityTypeName,
    IDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { CurrencyCode, DocumentTypeCode, ElectronicSubmissionTypeCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import { getUnreadMessagesCount } from "@pages/chats/Chats.utils";
import { shouldUseGrant } from "@pages/grant/Grant.utils";
import {
    employeeMonthlyBenefitsTileDef,
    employeePayrollTileDef,
    getEmployeeBenefitLimitsTileDef,
    getEmployeeCompensationTileDef,
    getEmployeeLeaveBalanceTileDef,
    getEmployeePayslipTileDef,
    getEmployeeSingleLeaveBalanceTileDef,
    getPayslipOverviewTileDef,
    isPayrollEnabledOnCompanyCallback
} from "@pages/home/EmployeeTiles";
import {
    getEmployeeOverviewTileDef,
    getEmploymentOverviewTileDef,
    getEmploymentsTableDef,
    getPROtherLiabilitiesTableTileDef,
    getSalaryAveragesTileDef,
    getTaxpayerDeclarationTileDef
} from "@pages/home/PayrollDashboradDef";
import {
    getCompanyCurrency,
    isAccountAssignmentCompanyValue,
    isCashBasisAccountingCompanyValue,
    isVatRegisteredCompany
} from "@utils/CompanyUtils";
import { forEachKey, ifPositive } from "@utils/general";
import { logger } from "@utils/log";
import i18next from "i18next";
import React from "react";

import {
    ASSET_DEPRECIATION_MONTH_API_URL,
    CBA_INCOME_EXPENSE_OVERVIEW_API_URL,
    EMPTY_DASH
} from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { IconSize, QueryParam, Status, TextAlign, ValueType } from "../../enums";
import { TRecordType } from "../../global.types";
import {
    ROUTE_ACCOUNT_ANALYSIS,
    ROUTE_ACCOUNTING_JOURNAL,
    ROUTE_ASSET_ANALYSIS,
    ROUTE_ATTENDANCE,
    ROUTE_BALANCE_SHEET,
    ROUTE_CBA_ENTRY_LEDGER,
    ROUTE_CBA_EXPENSES_AND_REVENUE,
    ROUTE_CHATS,
    ROUTE_CUSTOMER_PORTAL_PAYMENT,
    ROUTE_DOCUMENT_JOURNAL,
    ROUTE_EXPENSES_AND_REVENUE,
    ROUTE_GENERAL_LEDGER,
    ROUTE_GRANT_JOURNAL,
    ROUTE_INBOX_CUSTOMER_APPROVAL,
    ROUTE_INBOX_SEND_TO_ACCOUNTANT,
    ROUTE_INCOME_STATEMENT,
    ROUTE_PAYMENT_JOURNAL,
    ROUTE_SOCIAL_EVENTS
} from "../../routes";
import { formatCurrency, getCurrencyScale } from "../../types/Currency";
import DateType, { getUtcDate, getUtcDayjs, TDateLike } from "../../types/Date";
import customFetch, { getDefaultPostParams } from "../../utils/customFetch";
import memoize from "../../utils/memoize";
import { getUnpaidDocumentsForPayment } from "../customerPortalPayment/CustomerPortalPaymentUtils";
import { getLastSubmissionByType } from "../electronicSubmission/ElectronicSubmission.utils";
import { getActiveFiscalYear } from "../fiscalYear/FiscalYear.utils";
import { getInboxForApprovalCount } from "../inbox/Inbox.utils";
import {
    AgingType
} from "../reports/aging/AgingBase";
import {
    CbaIncomeExpenseType,
    IReportData,
    IReportSettings,
    NumberAggregationFunction,
    TimeAggregationFunction
} from "../reports/Report.utils";
import { PercentageWrapper, SemanticText } from "./Home.style";
import {
    getAccountAnalysisData, getAgingChartTileInfo,
    getAssetAnalysisData, getBankAccountsTileDef, getCashBoxTileDef,
    getChartLabels,
    getCreditAccountBalancesTileDef, getCurrentFinancialResultTileDef,
    getGrantTileDef,
    getIncomeExpenseOverviewTileDef,
    getInfoDataTileCellValueWithSeverity,
    getJournalSummaryData,
    getLiabilitiesToShareholdersTileDef,
    getLinkTileDef,
    getProfitLossTileDef,
    getVatTileDef,
    getWelcomeTileDef,
    isInCustomerPortal,
    timeAggregationToOpUnitType
} from "./Home.utils";

const _isVatPayer = (args: IGetValueArgs) => isVatRegisteredCompany(args.context);

export const reportLinkConfigs: TRecordType<TBasicLinkTileConfig> = {
    BalanceSheet: {
        link: ROUTE_BALANCE_SHEET,
        isVisible: isAccountAssignmentCompanyValue,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    IncomeStatement: {
        link: ROUTE_INCOME_STATEMENT,
        isVisible: isAccountAssignmentCompanyValue,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    ExpensesAndRevenue: {
        link: ROUTE_EXPENSES_AND_REVENUE,
        isVisible: isAccountAssignmentCompanyValue,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    GeneralLedger: {
        link: ROUTE_GENERAL_LEDGER,
        isVisible: isAccountAssignmentCompanyValue,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    AccountingJournal: {
        link: ROUTE_ACCOUNTING_JOURNAL,
        isVisible: isAccountAssignmentCompanyValue,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    DocumentJournal: {
        link: ROUTE_DOCUMENT_JOURNAL,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    PaymentJournal: {
        link: ROUTE_PAYMENT_JOURNAL,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    CbaEntryLedger: {
        link: ROUTE_CBA_ENTRY_LEDGER,
        isVisible: not(isAccountAssignmentCompanyValue),
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    CbaExpensesAndRevenue: {
        link: ROUTE_CBA_EXPENSES_AND_REVENUE,
        isVisible: not(isAccountAssignmentCompanyValue),
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    GrantJournal: {
        link: ROUTE_GRANT_JOURNAL,
        isVisible: (args) => shouldUseGrant(args.context),
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    },
    AccountAnalysis: {
        link: ROUTE_ACCOUNT_ANALYSIS,
        isVisible: not(isCashBasisAccountingCompanyValue),
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    }
};

export const linkConfigs: TRecordType<TBasicLinkTileConfig> = {
    Chats: {
        link: ROUTE_CHATS,
        count: getUnreadMessagesCount,
        CPs: [UserPermissionCodes.CustomerPortal, CustomerPermissionCodes.ChatInteractWithAccountant],
        iconName: "Tickets"
    },
    CustomerPortalPayment: {
        link: ROUTE_CUSTOMER_PORTAL_PAYMENT,
        count: getUnpaidDocumentsForPayment,
        CPs: [UserPermissionCodes.CustomerPortal, CustomerPermissionCodes.Payments]
    },
    Inbox: {
        link: ROUTE_INBOX_CUSTOMER_APPROVAL,
        count: getInboxForApprovalCount,
        CPs: [UserPermissionCodes.CustomerPortal, CustomerPermissionCodes.DocumentApproval]
    },
    SendToAccountant: {
        link: ROUTE_INBOX_SEND_TO_ACCOUNTANT,
        CPs: [UserPermissionCodes.CustomerPortal, CustomerPermissionCodes.ChatInteractWithAccountant]
    },
    // not ready, hidden
    // CustomerPortalInvoicing: { link: ROUTE_CONNECT_WITH_IDOKLAD },

    // Payroll items...
    Attendance: {
        link: ROUTE_ATTENDANCE,
        iconName: "Attendance",
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [UserPermissionCodes.CustomerPortal, UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrAttendance]
    },
    SocialEvents: {
        link: ROUTE_SOCIAL_EVENTS,
        iconName: "SocialEvents",
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [UserPermissionCodes.CustomerPortal, UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrAttendance]
    }
    // Todo: planned for future use, currently we don't support social requests in customer portal
    // SocialRequests: {
    //     link: ROUTE_SOCIAL_REQUESTS,
    //     iconName: "SocialEvents",
    //     isVisible: isPayrollEnabledOnCompanyCallback,
    //     CPs: CustomerPermissionCodes.PrPayrollAgenda
    // }
};

export const chatsWidgetId: keyof typeof linkConfigs = "Chats";
export const customerPortalPaymentId: keyof typeof linkConfigs = "CustomerPortalPayment";

export const getLinkTileConfig = (id: string, cfg: TBasicLinkTileConfig): ILinkTileInfo =>
    getLinkTileDef(`Home:CustomerPortalLink.${id}`, cfg.link, id, cfg);


// todo: unify with ExchangeRateDiffScreen.tsx
function getBestBusinessPartnerTableDataCellValues(name: string, thisYearValue: number, lastYearValue: number, context: IAppContext): TRecordType<TCellValue> {
    const formattedValue = formatCurrency(thisYearValue, getCompanyCurrency(context));

    const diff = thisYearValue - lastYearValue;
    const severity = ifPositive<Status>(diff, Status.Success, Status.Error, Status.Warning);
    const Icon = ifPositive(diff, GrowthPositiveIcon, GrowthNegativeIcon, GrowthZeroIcon);
    const percentage = thisYearValue !== 0 && lastYearValue !== 0 ? Math.abs(Math.round(100 * thisYearValue / lastYearValue - 100)) : 100;

    return {
        Name: {
            tooltip: name,
            value: (<strong>{name}</strong>)
        },
        Value: formattedValue,
        Difference: {
            tooltip: `${percentage} %`,
            value: (
                <PercentageWrapper>
                    <Icon width={IconSize.XS} status={severity}/>
                    <SemanticText severity={severity}>{percentage}&nbsp;%</SemanticText>
                </PercentageWrapper>
            )
        }
    };
}

const REVENUES_ACCOUNT_PREFIX = "6";
const EXPENSES_ACCOUNT_PREFIX = "5";

function getCbaEntryLedgerParams(interval: IDateInterval, aggregationFn?: TimeAggregationFunction, onlyIncome = false): IReportSettings {
    const Groups = aggregationFn ? [{
        ColumnAlias: "CbaIncomeExpenseOverview_DocumentDate",
        AggregationFunction: aggregationFn
    }] : [];

    const Type = onlyIncome ? CbaIncomeExpenseType.Income : CbaIncomeExpenseType.All;

    return {
        DateRange: {
            DateStart: formatDateToDateString(interval.from),
            DateEnd: formatDateToDateString(interval.to)
        },
        IncludeUnrelatedToBusiness: false,
        Type,
        ReportHierarchy: {
            Aggregate: true,
            Groups,
            Columns: [],
            Aggregations: [{
                ColumnAlias: "CbaIncomeExpenseOverview_AmountTaxed",
                AggregationFunction: NumberAggregationFunction.Sum
            }]
        }
    };
}

const getCbaEntryLedgerSummaryData = memoize(async (interval: IDateInterval, aggregationFn: TimeAggregationFunction, onlyIncome = false): Promise<number[]> => {
    const aggregatedPoints = getChartLabels(interval, aggregationFn, "YYYY-MM-DD");
    const opUnit = timeAggregationToOpUnitType(aggregationFn);

    const _extractAggregatedData = (rows: IReportRowDef[]): number[] => {
        return aggregatedPoints.map(formattedDate => {
            const groupedResult = rows?.find(row => row.Type === "MergedGroup" && getUtcDayjs(row.Value[`CbaIncomeExpenseOverview_DocumentDate_${aggregationFn}`] as TDateLike).isSame(getUtcDayjs(formattedDate), opUnit));
            return groupedResult ? groupedResult.Value[`CbaIncomeExpenseOverview_AmountTaxed_SUM`] as number : 0;
        });
    };

    let ret: number[] = [];

    try {
        const params = getCbaEntryLedgerParams(interval, aggregationFn, onlyIncome);

        ret = await customFetch(CBA_INCOME_EXPENSE_OVERVIEW_API_URL, {
            ...getDefaultPostParams(),
            body: JSON.stringify(params)
        })
            .then(response => response.json())
            .then((data: IReportData) => _extractAggregatedData(data.Rows));

    } catch (e) {
        logger.error(e);
    }

    return ret;
}, (interval, aggregationFn) => [interval.from, interval.to, aggregationFn]);

async function getJournalDataForCurrentFYForInfoTile(account: string, trPrefix: string, {
    context
}: IGetTileDataArgs) {
    const currentFY = getActiveFiscalYear(context);
    const currentFYInterval = {
        from: getUtcDayjs(currentFY?.DateStart),
        to: getUtcDayjs(currentFY?.DateEnd)
    };
    const [value] = await getJournalSummaryData(account, currentFYInterval);
    return [{
        ...getInfoDataTileCellValueWithSeverity(value),
        label: i18next.t(`Home:${trPrefix}.Label`),
        unit: "Kč"
    }];
}

async function getRevenueGrowthChartTileData(): Promise<IChartTileData<ChartType.Line>> {
    const dataSetLabels = ["LastYear", "ThisYear"].map(key => i18next.t(`Home:RevenueGrowth.${key}`));
    const step = TimeAggregationFunction.Month;
    const { previous, current } = getSlidingInterval(getUtcDayjs().endOf(timeAggregationToOpUnitType(step)), "year");
    const labels = getChartLabels(current, step);

    const values = await Promise.all([previous, current].map(interval => getAccountAnalysisData(REVENUES_ACCOUNT_PREFIX, interval, step)));

    return {
        type: ChartType.Line,
        chartProps: {
            labels,
            filled: true,
            dataSetLabels,
            colors: ["C_CHART_orange", "C_CHART_red"],
            values
        }
    };
}

async function getRevenueExpenseDiffChartTileData(): Promise<IChartTileData<ChartType.Line>> {
    const step = TimeAggregationFunction.Month;
    const { current } = getSlidingInterval(getUtcDayjs().endOf(timeAggregationToOpUnitType(step)), "year");
    const labels = getChartLabels(current, step);

    const [revenues, expenses] = await Promise.all([REVENUES_ACCOUNT_PREFIX, EXPENSES_ACCOUNT_PREFIX].map(account => getAccountAnalysisData(account, current, step)));
    const values = revenues.map((revenue, idx) => revenue - expenses[idx]);

    return {
        type: ChartType.Line,
        chartProps: {
            labels,
            colors: ["C_CHART_green"],
            values: [values]
        }
    };
}

async function getCbaRevenueGrowthChartTileData(): Promise<IChartTileData<ChartType.Line>> {
    const dataSetLabels = ["LastYear", "ThisYear"].map(key => i18next.t(`Home:CbaRevenueGrowth.${key}`));
    const step = TimeAggregationFunction.Month;
    const { previous, current } = getSlidingInterval(getUtcDayjs().endOf(timeAggregationToOpUnitType(step)), "year");
    const labels = getChartLabels(current, step);

    const values = await Promise.all([previous, current].map(interval => getCbaEntryLedgerSummaryData(interval, step, true)));

    return {
        type: ChartType.Line,
        chartProps: {
            labels,
            filled: true,
            dataSetLabels,
            colors: ["C_CHART_orange", "C_CHART_red"],
            values
        }
    };
}

async function getCbaRevenueExpenseDiffChartTileData(): Promise<IChartTileData<ChartType.Line>> {
    const step = TimeAggregationFunction.Month;
    const { current } = getSlidingInterval(getUtcDayjs().endOf(timeAggregationToOpUnitType(step)), "year");
    const labels = getChartLabels(current, step);

    const values = await getCbaEntryLedgerSummaryData(current, step);

    return {
        type: ChartType.Line,
        chartProps: {
            labels,
            colors: ["C_CHART_green"],
            values: [values]
        }
    };
}

async function getBusinessPartnersTableData(oData: OData, context: IAppContext, interval: IDayjsInterval, types: DocumentTypeCode[] = [], businessPartnerIds: number[] = null): Promise<Pick<IDocumentEntity, "BusinessPartner" | "Amount">[]> {
    const bpPath = createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.BusinessPartner, BusinessPartnerEntity.Id);
    const filters = [
        `${DocumentEntity.DateAccountingTransaction} ge ${transformToODataString(interval.from, ValueType.Date)}`,
        `${DocumentEntity.DateAccountingTransaction} le ${transformToODataString(interval.to, ValueType.Date)}`,
        `${bpPath} ne null`,
    ];
    const resultCount = 5;
    const isVatPayer = _isVatPayer({ context });

    if (types?.length) {
        const docTypesQuery = transformToODataString(types, ValueType.String);
        filters.push(`${DocumentEntity.DocumentTypeCode} in (${docTypesQuery})`);
    }

    businessPartnerIds = businessPartnerIds?.filter(item => !!item);
    if (businessPartnerIds?.length) {
        filters.push(`${bpPath} in (${transformToODataString(businessPartnerIds, ValueType.Number)})`);
    }

    const query = oData.getEntitySetWrapper(EntitySetName.Documents).query()
        .groupBy(
            createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.BusinessPartner, BusinessPartnerEntity.Id),
            createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.BusinessPartner, BusinessPartnerEntity.Name)
        )
        .aggregate(`${isVatPayer ? DocumentEntity.AmountNet : DocumentEntity.Amount} with sum as ${DocumentEntity.Amount}`)
        .orderBy(DocumentEntity.Amount, false)
        .top(resultCount)
        .filter(filters.join(" AND "));

    const data = await query.fetchData<IDocumentEntity[]>();

    return data.value.map(item => ({ ...item, ...item.BusinessPartner }));
}

export async function getBestBusinessPartnersTableData(key: "BestSuppliers" | "BestCustomers", {
    oData,
    context
}: IGetTileDataArgs): Promise<ITableTileData> {
    const columns: IColumn[] = [{
        id: "Name",
        label: i18next.t(`Home:${key}.Name`),
        textAlign: TextAlign.Left,
        width: 165
    }, {
        id: "Value",
        label: i18next.t(`Home:${key}.Value`),
        textAlign: TextAlign.Right
    }, {
        id: "Difference",
        label: i18next.t(`Home:${key}.Difference`),
        textAlign: TextAlign.Right,
        stretchContent: true
    }];
    const rows: IRow[] = [];

    const isExpense = key === "BestSuppliers";
    const docTypes = isExpense ? [DocumentTypeCode.InvoiceReceived, DocumentTypeCode.OtherLiability, DocumentTypeCode.CashReceiptReceived, DocumentTypeCode.CorrectiveInvoiceReceived]
        : [DocumentTypeCode.InvoiceIssued, DocumentTypeCode.OtherReceivable, DocumentTypeCode.CashReceiptIssued, DocumentTypeCode.CorrectiveInvoiceIssued];

    const { current, previous } = getSlidingInterval(getUtcDayjs(), "year");
    const currentData = await getBusinessPartnersTableData(oData, context, current, docTypes);

    if (currentData.length) {
        const BPIds = currentData.map(doc => doc.BusinessPartner.Id);
        const previousData = await getBusinessPartnersTableData(oData, context, previous, docTypes, BPIds);

        currentData.forEach(row => {
            const { Id, Name } = row.BusinessPartner;
            const Value = row.Amount ?? 0;
            const Difference = previousData.find(prevRow => prevRow.BusinessPartner.Id === Id)?.Amount ?? 0;

            rows.push({ id: Id, values: getBestBusinessPartnerTableDataCellValues(Name, Value, Difference, context) });
        });
    }

    return { columns, rows };
}

interface IAssetDepreciationSummary {
    CurrentMonthDepreciation: number;
    PreviousMonthDepreciation: number;
}

async function getAssetDepreciationData(): Promise<IAssetDepreciationSummary> {
    let data: IAssetDepreciationSummary;
    try {
        const response = await customFetch(ASSET_DEPRECIATION_MONTH_API_URL);
        data = await response.json();
    } catch (e) {
        logger.error(e);
    }
    return {
        CurrentMonthDepreciation: data?.CurrentMonthDepreciation ?? 0,
        PreviousMonthDepreciation: data?.PreviousMonthDepreciation ?? 0
    };
}

export const currencyScaleFormatter: TChartValueFormatterFn = (axis, value, idx) => {
    if (axis === "y") {
        return getCurrencyScale(value as number, CurrencyCode.CzechKoruna);
    } else if (!axis) {
        // exact value
        return formatCurrency(value, CurrencyCode.CzechKoruna);
    }
    return `${value}`;
};

/**
 * Dashboard configuration for customer - visible on customer portal and on agenda level
 */
export function getDashboardDefinition(): IDashboardDefinition {

    const linkTiles: TTileDefinition = {};
    forEachKey(linkConfigs, (id) => {
        linkTiles[id] = getLinkTileConfig(id, linkConfigs[id]);
    });

    const groupConfigs = Object.keys(reportLinkConfigs).map(key => {
        return getLinkTileConfig(key, reportLinkConfigs[key]);
    });

    const tileDefinition: TTileDefinition = {
        Welcome: getWelcomeTileDef(),
        ...linkTiles,
        IncomeExpenseOverview: {
            ...getIncomeExpenseOverviewTileDef(),
            CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
        },
        LastSubmission: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:LastSubmission.Heading"),
            infoData: async ({ oData, context }) => {
                const lastSubmission = await getLastSubmissionByType(oData, ElectronicSubmissionTypeCode.VatStatement);
                return [{
                    value: lastSubmission?.DateSubmission ? DateType.format(lastSubmission?.DateSubmission) : EMPTY_DASH,
                    label: lastSubmission?.ElectronicSubmissionTypeCode ? getEnumDisplayValue(EntityTypeName.ElectronicSubmissionType, lastSubmission?.ElectronicSubmissionTypeCode)
                        : i18next.t("Home:LastSubmission.None")
                }];
            },
            isVisible: ifAll(_isVatPayer, isAccountAssignmentCompanyValue),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            size: { w: 2, h: 1 }
        },
        AssetAnalysis: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:AssetAnalysis.Heading"),
            link: ROUTE_ASSET_ANALYSIS,
            infoData: async ({ context }) => {
                const fy = getActiveFiscalYear(context);
                const value = await getAssetAnalysisData(fy?.Id);
                return [{
                    value,
                    label: i18next.t("Home:AssetAnalysis.Label"),
                    unit: "Kč"
                }];
            },
            formatter: infoTileCurrencyFormatter,
            isVisible: not(isAccountAssignmentCompanyValue),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            size: { w: 2, h: 1 }
        },
        VatOverview: getVatTileDef(),
        Expenses: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:Expenses.Heading"),
            infoData: getJournalDataForCurrentFYForInfoTile.bind(null, EXPENSES_ACCOUNT_PREFIX, "Expenses"),
            formatter: infoTileCurrencyFormatter,
            isVisible: isAccountAssignmentCompanyValue,
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            size: { w: 2, h: 1 }
        },
        Reporting: {
            type: DashboardTileType.Group,
            title: i18next.t("Home:Reports"),
            size: { w: 1, h: 1 },
            items: groupConfigs,
            CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
        },
        Revenues: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:Revenues.Heading"),
            infoData: getJournalDataForCurrentFYForInfoTile.bind(null, REVENUES_ACCOUNT_PREFIX, "Revenues"),
            formatter: infoTileCurrencyFormatter,
            isVisible: isAccountAssignmentCompanyValue,
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            size: { w: 2, h: 1 }
        },
        // AverageMaturity: {
        //     type: DashboardTileType.Info,
        //     title: i18next.t("Home:AverageMaturity.Heading"),
        //     infoData: async ({ oData, context }) => {
        //         const revenues = 14,
        //             expenses = 7;
        //         return [{
        //             value: revenues,
        //             label: i18next.t("Home:AverageMaturity.Revenues"),
        //             unit: i18next.t("Home:AverageMaturity.day", { count: revenues })
        //         }, {
        //             value: expenses,
        //             label: i18next.t("Home:AverageMaturity.Expenses"),
        //             unit: i18next.t("Home:AverageMaturity.day", { count: expenses })
        //         }];
        //     },
        //     size: { w: 2, h: 1 }
        // },
        AssetDepreciation: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:AssetDepreciation.Heading"),
            link: ROUTE_ASSET_ANALYSIS,
            isVisible: isAccountAssignmentCompanyValue,
            infoData: async ({ oData, context }) => {
                const { CurrentMonthDepreciation, PreviousMonthDepreciation } = await getAssetDepreciationData();
                return [{
                    value: CurrentMonthDepreciation,
                    label: i18next.t("Home:AssetDepreciation.ThisMonth"),
                    unit: "Kč"
                }, {
                    value: PreviousMonthDepreciation,
                    label: i18next.t("Home:AssetDepreciation.NextMonth"),
                    unit: "Kč"
                }];
            },
            formatter: infoTileCurrencyFormatter,
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            size: { w: 4, h: 1 }
        },
        BestSuppliers: {
            type: DashboardTileType.Table,
            title: i18next.t("Home:BestSuppliers.Heading"),
            size: { w: 3, h: 2 },
            tooltip: i18next.t("Home:BestSuppliers.Tooltip"),
            tableData: getBestBusinessPartnersTableData.bind(null, "BestSuppliers"),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
        },
        Grants: {
            ...getGrantTileDef(),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
        },
        ProfitLoss: getProfitLossTileDef(),
        CurrentFinancialResult: getCurrentFinancialResultTileDef(),
        CreditAccountBalances: getCreditAccountBalancesTileDef(),
        LiabilitiesToShareholders: getLiabilitiesToShareholdersTileDef(),
        BestCustomers: {
            type: DashboardTileType.Table,
            title: i18next.t("Home:BestCustomers.Heading"),
            size: { w: 3, h: 2 },
            tooltip: i18next.t("Home:BestCustomers.Tooltip"),
            tableData: getBestBusinessPartnersTableData.bind(null, "BestCustomers"),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
        },
        AgingAP: getAgingChartTileInfo(AgingType.AP),
        AgingAR: getAgingChartTileInfo(AgingType.AR),
        BankAccounts: getBankAccountsTileDef(),
        CashBoxes: getCashBoxTileDef(),
        RevenueGrowth: {
            type: DashboardTileType.Chart,
            title: i18next.t("Home:RevenueGrowth.Heading"),
            size: { w: 4, h: 2 },
            chartData: getRevenueGrowthChartTileData,
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            formatter: currencyScaleFormatter,
            isVisible: isAccountAssignmentCompanyValue
        },
        RevenueExpenseDiff: {
            type: DashboardTileType.Chart,
            title: i18next.t("Home:RevenueExpenseDiff.Heading"),
            size: { w: 4, h: 2 },
            chartData: getRevenueExpenseDiffChartTileData,
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
            formatter: currencyScaleFormatter,
            isVisible: isAccountAssignmentCompanyValue
        },
        CbaRevenueGrowth: {
            type: DashboardTileType.Chart,
            title: i18next.t("Home:CbaRevenueGrowth.Heading"),
            size: { w: 4, h: 2 },
            chartData: getCbaRevenueGrowthChartTileData,
            formatter: currencyScaleFormatter,
            isVisible: not(isAccountAssignmentCompanyValue),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
        },
        CbaRevenueExpenseDiff: {
            type: DashboardTileType.Chart,
            title: i18next.t("Home:CbaRevenueExpenseDiff.Heading"),
            size: { w: 4, h: 2 },
            chartData: getCbaRevenueExpenseDiffChartTileData,
            formatter: currencyScaleFormatter,
            isVisible: not(isAccountAssignmentCompanyValue),
            CPs: [CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.CustomerPortal],
        },
        EmployeeOverview: getEmployeeOverviewTileDef(),
        TaxPayerDeclarationOverview: getTaxpayerDeclarationTileDef(),
        EmploymentOverview: getEmploymentOverviewTileDef(),
        SalaryAverages: getSalaryAveragesTileDef(),
        EmploymentsTable: getEmploymentsTableDef(),
        PROtherLiabilitiesTable: getPROtherLiabilitiesTableTileDef(),

        // Payroll tiles for accountant or user with higher permissions
        EmployeePayslip: getEmployeePayslipTileDef(),
        PayslipOverview: getPayslipOverviewTileDef(),

        // EmployeePortal tiles
        //  todo: split into separate group so the personal tiles are not mixed with the company ones??
        EmployeeCompensation: getEmployeeCompensationTileDef(),
        EmployeePayroll: employeePayrollTileDef(),
        EmployeeMonthlyBenefits: employeeMonthlyBenefitsTileDef(),
        BenefitLimits: getEmployeeBenefitLimitsTileDef(),
        LeaveBalance: getEmployeeLeaveBalanceTileDef(),
        SingleLeaveBalance: getEmployeeSingleLeaveBalanceTileDef(),
    };

    const optionalTiles: (keyof TTileDefinition)[] = [];
    const defaultTiles = Object.keys(tileDefinition).filter(key => !optionalTiles.includes(key));

    return {
        groups: [
            {
                id: "all",
                title: i18next.t("Home:CustomerTitle"),
                tiles: defaultTiles,
                allTiles: [...defaultTiles, ...optionalTiles]
            }
        ],
        tileDefinition,
        isVisible: isInCustomerPortal
    };
}
